import React, { useState, useEffect, useContext, useRef } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import FileChooser from "./FileChooser";
import Tools from "./JNSTools";
import { CredsContext } from "./Contexts";
import { Box } from "@mui/material";
import Typography from "@mui/material/Typography";
import {
  ColumnDirective,
  ColumnsDirective,
  Filter,
  GridComponent,
  Group,
} from "@syncfusion/ej2-react-grids";
import { Inject, Page, Sort } from "@syncfusion/ej2-react-grids";

function RequirementsImportScreen(props) {
  const [dataReturned, setDataReturned] = useState({
    status: ".",
    dataSet: null,
  });

  const [loadFinished, setLoadFinished] = useState(false);
  const creds = useContext(CredsContext).creds;
  const projectObject = JSON.parse(sessionStorage.getItem("projectObject"));
  const REQUIREMENT_ISSUE_CATEGORY_ID = useRef();
  const REQUIREMENT_VERSION_ISSUE_CATEGORY_ID = useRef();
  const APPROVED_STAGE_ID = useRef();
  const PROPOSED_STAGE_ID = useRef();
  const loadedRows = useRef(0);
  const validRows = useRef(0);

  let columnTitles = [];
  let lengths = [];

  const oadaSettingsRaw = localStorage.getItem("oadaSettings");
  if (oadaSettingsRaw) {
    const oadaSettings = JSON.parse(oadaSettingsRaw);
    REQUIREMENT_ISSUE_CATEGORY_ID.current =
      oadaSettings["requirementCategoryDropdown_options"];
    REQUIREMENT_VERSION_ISSUE_CATEGORY_ID.current =
      oadaSettings["requirementVersionsCategoryDropdown_options"];
    APPROVED_STAGE_ID.current =
      oadaSettings["approvedRequirementStageDropdown_options"];
    PROPOSED_STAGE_ID.current =
      oadaSettings["proposedRequirementStageDropdown_options"];
  }
  const handleChange = async (file) => {
    try {
      // Read the file as Base64
      const fileContent = await readFileAsync(file);

      // Parse CSV data into structured format
      const { columnTitles, fullDataSet } = parseCSVData(fileContent);

      // Validate the CSV structure
      const validation = validateFileStructure(columnTitles);

      if (!validation.isValid) {
        setDataReturned({
          status: "invalid",
          message: validation.message,
          dataSet: null,
        });
        return;
      }

      // If valid, update state with parsed dataset
      setDataReturned({
        status: "valid",
        message: "Valid file structure, loading requirements",
        dataSet: fullDataSet,
      });
    } catch (error) {
      console.error("File processing error:", error);
      setDataReturned({
        status: "error",
        message: "Error reading or parsing file.",
        dataSet: null,
      });
    }
  };

  // Helper function to read file as Base64
  const readFileAsync = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(window.atob(reader.result.substring(21))); // Decode Base64
      reader.onerror = reject;
    });
  };

  // Helper function to parse CSV data
  const parseCSVData = (csvString) => {
    const dataTable = Tools.CSVParse(csvString);
    const columnTitles = dataTable[0].map((title) => title.toLowerCase());
    let fullDataSet = [];

    for (let i = 1; i < dataTable.length; i++) {
      let row = {};
      dataTable[i].forEach((cell, j) => {
        row[columnTitles[j]] = cell;
      });
      fullDataSet.push(row);
    }

    return { columnTitles, fullDataSet };
  };

  // Helper function to validate CSV structure
  const validateFileStructure = (columnTitles) => {
    const expectedColumns = ["category", "details", "stage", "synopsis"];
    const isValid =
      JSON.stringify(columnTitles.sort()) ===
      JSON.stringify(expectedColumns.sort());

    return isValid
      ? { isValid: true, message: "" }
      : {
          isValid: false,
          message:
            "Invalid file structure. Ensure the file has exactly four fields: category, synopsis, stage, details.",
        };
  };

  useEffect(() => {
    if (dataReturned.status == "valid") {
      const projRequirementCategories =
        projectObject.oada_req_categories__c.split("||");
      let newCategoryFound = false;
      //load reqs
      console.log("loading");
      const authString = Tools.getXmlBodyAuthString(creds);
      const urlString = Tools.getInstanceUrl(creds);
      const userFullName = creds.name;
      const userFirstName = userFullName
        .substring(userFullName.indexOf(",") + 1)
        .trim();
      const userLastName = userFullName.substring(0, userFullName.indexOf(","));

      const totalRows = dataReturned.dataSet.length;
      loadedRows.current = 0;
      validRows.current = 0;
      for (let reqRow = 0; reqRow < totalRows; reqRow++) {
        const validStage = validateStage(
          dataReturned.dataSet[reqRow]["stage"].toLowerCase(),
        );
        const validCategory = validateCategory(
          dataReturned.dataSet[reqRow]["category"].toLowerCase(),
        );

        const validDescription =
          dataReturned.dataSet[reqRow]["synopsis"].length > 0;

        if (validStage && validDescription && validCategory) {
          if (
            projRequirementCategories.indexOf(
              dataReturned.dataSet[reqRow]["category"],
            ) == -1
          ) {
            projRequirementCategories.push(
              dataReturned.dataSet[reqRow]["category"],
            );
            newCategoryFound = true;
          }
          let changeMessage =
            new Date().getTime() +
            "|" +
            userFirstName +
            " " +
            userLastName +
            ":c:CREATED:f::t::o:" + // on
            new Date().toDateString() +
            " at " +
            new Date().toLocaleTimeString("en-US", {
              timeZone: "America/New_York",
            }) +
            " ET||";

          const stageId =
            dataReturned.dataSet[reqRow]["stage"].toLowerCase() == "approved"
              ? APPROVED_STAGE_ID.current
              : PROPOSED_STAGE_ID.current;

          const addReqString =
            authString +
            '<Add type="Issue" enable_custom="1"><Issue><date>' +
            Tools.parseDateObject(new Date()) +
            "</date><issue_stage_id>" +
            stageId +
            "</issue_stage_id><issue_category_id>" +
            REQUIREMENT_ISSUE_CATEGORY_ID.current +
            "</issue_category_id><oada_req_category__c>" +
            dataReturned.dataSet[reqRow]["category"] +
            "</oada_req_category__c><oada_req_category_name__c>" +
            dataReturned.dataSet[reqRow]["category"] +
            "</oada_req_category_name__c><description>" +
            dataReturned.dataSet[reqRow]["synopsis"].replace(/&/g, "&amp;") +
            "</description><oada_req_details__c>" +
            Tools.encodeHTML(dataReturned.dataSet[reqRow]["details"]) +
            "</oada_req_details__c><owner_id>" +
            creds.id +
            "</owner_id><project_id>" +
            projectObject.id +
            "</project_id><oada_issue_change_log__c>" +
            changeMessage +
            "</oada_issue_change_log__c></Issue></Add></request>";

          //console.log(addReqString);

          Tools.fetchPost(addReqString, urlString).then(() => {
            validRows.current++;
            loadedRows.current++;
            if (loadedRows.current == totalRows) {
              if (newCategoryFound) {
                projRequirementCategories.sort();
                const projRequirementCategoriesString =
                  projRequirementCategories.join("||");
                const requestString =
                  authString +
                  '<Modify type="Project" enable_custom="1"><Project><id>' +
                  projectObject.id +
                  "</id><oada_req_categories__c>" +
                  projRequirementCategoriesString +
                  "</oada_req_categories__c></Project></Modify></request>";
                projectObject.oada_req_categories__c =
                  projRequirementCategoriesString;
                sessionStorage.setItem(
                  "projectObject",
                  JSON.stringify(projectObject),
                );
                Tools.fetchPost(requestString, urlString).then(() => {
                  setLoadFinished(true);
                });
              } else {
                setLoadFinished(true);
              }
            }
          });
        } else {
          loadedRows.current++;
        }
      }
    }
  }, [dataReturned]);
  /*
  let totalLength = 0;
  if (lengths.length > 0) {
    for (let k = 0; k < lengths.length; k++) {
      totalLength += lengths[k];
    }
  }

*/

  function validateStage(stage) {
    let valid = false;
    if (stage == "proposed" || stage == "approved") {
      valid = true;
    }
    return valid;
  }

  function validateCategory(category) {
    let valid = false;
    return true;
  }

  return (
    <div>
      <Box
        sx={{
          backgroundColor: "gainsboro",
          border: "solid 1px",
          borderRadius: "3px",
          padding: "5px",
          textAlign: "center",
        }}
      >
        <Typography>Select a file containing requirements to import</Typography>
        <Typography
          sx={{ color: "red", fontSize: "small", fontStyle: "italic" }}
        >
          Note that the current version of this import engine requires the file
          to be a .csv file with exactly four fields:
          <p>category, synopsis, stage, details</p>
          The category and detail fields may be empty, but they still need to
          exist in the file.<br></br>
          <br></br>
        </Typography>
        <FileChooser
          id="fileChooserRequirementsImport"
          changeHandler={handleChange}
          fileExtension=".csv"
          isHidden={false}
          isDisabled={loadFinished ? true : false}
        />

        <p>{dataReturned.message}</p>
        {loadFinished ? (
          <p>
            Requirements load completed. Loaded {validRows.current} out of{" "}
            {loadedRows.current} rows
          </p>
        ) : null}
        <div style={{ textAlign: "center" }}>
          <Button
            onClick={props.handleClose}
            className="dialogConfirmCancelButton"
          >
            {loadFinished ? "Close" : "Cancel"}
          </Button>
        </div>
      </Box>
    </div>
  );
}

export default RequirementsImportScreen;

RequirementsImportScreen.propTypes = {
  handleClose: PropTypes.func,
};

/*
      <GridComponent dataSource={dataReturned} width={600}>
        <Inject services={[Page, Sort, Filter, Group]} />
      </GridComponent>
      */
